import {
	BanIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
	CheckIcon,
	Dialog,
	DropdownDatePicker,
	Input,
	RadioInput,
	Schedule,
	SkinnyArrowLeft,
	SkinnyArrowRight,
	ToggleButton,
	useOpen,
} from '@aleph/shared-components';
import {
	useMemo, useRef, useState,
} from 'react';
import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import dayjs from 'dayjs';
import useTransformSpotifySchedule from 'wb-spotify/app/routes/reports/create/useTransformSchedule';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { SCHEDULER_OPTIONS } from 'wb-spotify/app/routes/utils';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';
import { getDatesFromSchedule } from 'wb-spotify/app/routes/reports/utils';

const SpotifyReportScheduler = () => {
	const { t } = useTranslation();
	const {
		schedule,
		setSchedule,
		dates,
		setDates,
		time,
		setTime,
	} = useCreateSpotifyReport();
	const [selectedSchedule, setSelectedSchedule] = useState<SpotifyReportSchedule>(
		schedule ?? SpotifyReportSchedule.OnlyOnce,
	);
	const [open, { onOpen, onClose }] = useOpen();
	const { lifetimeStats } = useSupergridSettings();

	const initialTime = useMemo(() => time, []);

	const maxDate = useRef(dayjs());
	const minYear = useRef(dates?.from?.subtract(5, 'years').year());

	const {
		scheduleTimeValue, scheduleTimeUnit, scheduleName,
	} = useTransformSpotifySchedule(selectedSchedule);

	const setPreviousDatesOfSchedule = () => {
		if (scheduleTimeUnit && scheduleTimeValue) {
			setDates(currDates => (currDates
				? {
					from: currDates.from?.subtract(scheduleTimeValue, scheduleTimeUnit),
					to:   currDates.to?.subtract(scheduleTimeValue, scheduleTimeUnit),
				}
				: currDates
			));
		}
	};

	const setNextDatesOfSchedule = () => {
		if (scheduleTimeUnit && scheduleTimeValue) {
			setDates(currDates => (currDates
				? {
					from:	currDates.from?.add(scheduleTimeValue, scheduleTimeUnit),
					to:   currDates.to?.add(scheduleTimeValue, scheduleTimeUnit),
				}
				: currDates
			));
		}
	};

	const onConfirm = () => {
		if (!selectedSchedule) {
			setSchedule(SpotifyReportSchedule.OnlyOnce);
		} else {
			setSchedule(selectedSchedule);
		}

		const newDates = getDatesFromSchedule({ selectedSchedule, maxDate: maxDate.current });

		setDates(newDates);

		onClose();
	};

	return (
		<div className='shrink-0'>
			<div className={classNames('flex w-full h-full py-[16px] items-center justify-end gap-[16px]')}>
				<DropdownDatePicker
					range
					maxDate={maxDate.current}
					maxYear={dayjs().year()}
					minYear={minYear.current}
					onChange={newDates => {
						if (newDates) {
							setDates(newDates);
						}
					}}
					value={dates}
					disabled={lifetimeStats}
					pressEnterToConfirm
				/>

				{
					schedule
					&& <div className="flex gap-[16px]">
						<ToggleButton
							onClick={setPreviousDatesOfSchedule}
							leadingIcon={<SkinnyArrowLeft />}
							disabled={lifetimeStats}
						/>

						<ToggleButton
							onClick={setNextDatesOfSchedule}
							leadingIcon={<SkinnyArrowRight />}
							disabled={lifetimeStats}
						/>
					</div>
				}

				<ToggleButton
					onClick={onOpen}
					leadingIcon={<Schedule />}
					active={open}
					data-testid="reports-scheduler-button"
				>
					{
						(schedule !== undefined)
							? `${SCHEDULER_OPTIONS.find(
								option => option.value === selectedSchedule,
							)?.label || 'Only once'}`
							: 'Schedule'
					}
				</ToggleButton>
			</div>
			<Dialog
				onClose={() => {
					onClose();
					setSelectedSchedule(schedule ?? SpotifyReportSchedule.OnlyOnce);
					setTime(initialTime);
				}}
				isOpen={open}
			>
				<Dialog.Header
					preset={{
						prefix: <Schedule />,
						title:  t('re: update schedule', 'Update schedule'),
					}}
				/>
				<Dialog.Content className="pb-[10px]">
					<div className="flex gap-[16px] py-[11.5px] mb-2">
						{SCHEDULER_OPTIONS.map(scheduleOption => (
							<RadioInput
								key={scheduleOption.value}
								labelProps={{ children: scheduleOption.label }}
								onClick={() => setSelectedSchedule(scheduleOption.value)}
								checked={selectedSchedule === scheduleOption.value}
							/>
						))
						}
					</div>

					{
						scheduleName
						&& <div className="text-[12px] flex items-center gap-4 py-1 mb-4">
							{
								selectedSchedule === SpotifyReportSchedule.OnlyOnce
									? (
										<>
											At <Input
												type="time"
												defaultValue={time}
												onChange={e => setTime(e.target.value)}
												inputClassName="items-center"
											/> (UTC+1)
										</>
									)
									: (
										<>
											Every <span className="font-bold text-black">{scheduleName}</span> at 4 a.m. (UTC+1)
										</>
									)
							}
						</div>
					}
				</Dialog.Content>
				<Dialog.Footer>
					<Button
						title={t('sc: discard changes', 'Discard changes')}
						leftNode={<BanIcon />}
						appearance={ButtonAppearance.GHOST}
						color={ButtonColor.RED}
						onClick={() => {
							onClose();
							setSelectedSchedule(schedule ?? SpotifyReportSchedule.OnlyOnce);
							setTime(initialTime);
						}}
					/>
					<Button
						title={t('sc: confirm', 'Confirm')}
						leftNode={<CheckIcon />}
						onClick={onConfirm}
					/>
				</Dialog.Footer>
			</Dialog>
		</div>
	);
};

export default SpotifyReportScheduler;
